import {
    AnyAction,
    createSlice,
    Dispatch,
    PayloadAction,
    ThunkAction,
    UnknownAction,
} from '@reduxjs/toolkit';

import { setFacilityId } from './configSlice';
import { RootState } from './index';
import { AUTO_OPEN_DRAWER_PRIORITY, AutoOpenDrawer } from 'src/constants/AutoOpenDrawers';
import { AllowBodyScrolling, PreventBodyScrolling } from 'src/utils/uiUtils';

type GlobalUIState = {
    backButtonOverride: Record<string, string>;
    hasOpenedDriftDrawer: boolean;
    lastFacilityId?: string;
    lastViewedRecognitionCreateTime: number;
    nowMs: number;
    pageScroll: Record<string, number>;
    preventBodyScrolling: boolean;
    requestedAutoOpenDrawers: AutoOpenDrawer[];
    shiftMismatchSinceMs: number | null;
};

export const initialState: GlobalUIState = {
    backButtonOverride: {},
    hasOpenedDriftDrawer: false,
    lastFacilityId: undefined,
    lastViewedRecognitionCreateTime: 0,
    nowMs: Date.now(),
    pageScroll: {},
    preventBodyScrolling: false,
    requestedAutoOpenDrawers: [],
    shiftMismatchSinceMs: null,
};

export const globalUISlice = createSlice({
    name: 'globalUI',
    initialState,
    reducers: {
        setPreventBodyScrolling: (state, { payload }) => {
            state.preventBodyScrolling = payload;
        },
        setPageScroll: (state, { payload }) => {
            const { page, scroll } = payload || {};
            if (page) {
                state.pageScroll[page] = scroll;
            }
        },
        setBackButtonOverride: (state, { payload }) => {
            const { page, override } = payload || {};
            if (page) {
                state.backButtonOverride[page] = override;
            }
        },
        setLastViewedRecognitionCreateTime: (state, { payload }) => {
            state.lastViewedRecognitionCreateTime = payload;
        },
        setNowMs: (state, { payload }: PayloadAction<number>) => {
            state.nowMs = payload;
        },
        setHasOpenedDriftDrawer: (state, { payload }: PayloadAction<boolean>) => {
            state.hasOpenedDriftDrawer = payload;
        },
        setShiftMismatchSinceMs: (state, { payload }: PayloadAction<number | null>) => {
            state.shiftMismatchSinceMs = payload;
        },
        requestAutoOpenDrawer: (state, { payload }: PayloadAction<AutoOpenDrawer>) => {
            if (!state.requestedAutoOpenDrawers.includes(payload)) {
                state.requestedAutoOpenDrawers.push(payload);
            }
        },
        dismissAutoOpenDrawer: (state, { payload }: PayloadAction<AutoOpenDrawer>) => {
            state.requestedAutoOpenDrawers = state.requestedAutoOpenDrawers.filter(
                (id) => id !== payload
            );
        },
    },
    // resets the requested auto-open drawers if and only if we switch facilities
    extraReducers: (builder) => {
        builder.addCase(setFacilityId, (state, { payload }) => {
            if (payload !== state.lastFacilityId) {
                state.requestedAutoOpenDrawers = [];
                state.lastFacilityId = payload;
            }
        });
    },
    selectors: {
        selectActiveAutoOpenDrawer: (sliceState: GlobalUIState) =>
            sliceState.requestedAutoOpenDrawers.reduce<AutoOpenDrawer | undefined>(
                (first, id) =>
                    first === undefined ||
                    AUTO_OPEN_DRAWER_PRIORITY[id] < AUTO_OPEN_DRAWER_PRIORITY[first]
                        ? id
                        : first,
                undefined
            ),
        selectBackButtonOverrideMap: (sliceState: GlobalUIState) => sliceState.backButtonOverride,
        selectHasOpenedDriftDrawer: (sliceState: GlobalUIState) => sliceState.hasOpenedDriftDrawer,
        selectIsPreventingBodyScrolling: (sliceState: GlobalUIState) =>
            sliceState.preventBodyScrolling,
        selectLastViewedRecognitionCreateTime: (sliceState: GlobalUIState) =>
            sliceState.lastViewedRecognitionCreateTime,
        selectNowMs: (sliceState: GlobalUIState) => sliceState.nowMs,
        selectPageScrollMap: (sliceState: GlobalUIState) => sliceState.pageScroll,
        selectShiftMismatchSinceMs: (sliceState: GlobalUIState) => sliceState.shiftMismatchSinceMs,
    },
});

// Action creators are generated for each case reducer function
export const {
    dismissAutoOpenDrawer,
    requestAutoOpenDrawer,
    setBackButtonOverride,
    setHasOpenedDriftDrawer,
    setLastViewedRecognitionCreateTime,
    setNowMs,
    setPageScroll,
    setPreventBodyScrolling,
    setShiftMismatchSinceMs,
} = globalUISlice.actions;

export const {
    selectActiveAutoOpenDrawer,
    selectBackButtonOverrideMap,
    selectHasOpenedDriftDrawer,
    selectIsPreventingBodyScrolling,
    selectLastViewedRecognitionCreateTime,
    selectNowMs,
    selectPageScrollMap,
    selectShiftMismatchSinceMs,
} = globalUISlice.selectors;

// Thunks
type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, UnknownAction>;

export function ShouldPreventBodyScrolling(
    booleanValue: boolean,
    scrollPosition: number = 0
): AppThunk {
    return (dispatch: Dispatch<AnyAction>, getState: () => RootState) => {
        const stateBefore = getState();
        if (stateBefore.globalUI.preventBodyScrolling !== booleanValue) {
            if (booleanValue) {
                PreventBodyScrolling(scrollPosition);
            } else {
                AllowBodyScrolling();
            }
            dispatch(setPreventBodyScrolling(booleanValue));
        }
    };
}

export default globalUISlice.reducer;
